/**
 * /admin/matches/:matchId/video-tagging — manual per-ball tagger.
 *
 * Operator flow:
 *   1. Upload the full-match MP4 (multipart-to-S3; skipped if already
 *      present — the operator just picks the same local file so the
 *      browser <video> element can play it).
 *   2. Ball-by-ball table shows every Ball on the match, with a
 *      "currently tagging" marker on the row we're on.
 *   3. Scrub the HTML5 player + press "Clip IN" and "Clip OUT" to
 *      stamp the ball's video window. Keyboard: [ = in, ] = out,
 *      Space = play/pause, Enter = save & next.
 *   4. Sequential (clip 1 → ball 1, clip 2 → ball 2, …). Double-click
 *      any row to jump back and re-record it.
 *   5. When every ball is tagged, "Slice & upload" fires the backend
 *      /tag-slice/ endpoint; the existing zcd-ball-slicer Lambda cuts
 *      per-ball MP4s at their deterministic S3 keys — same downstream
 *      pipeline as the math + OCR aligners.
 *
 * Backend:
 *   GET  /api/admin/v2/matches/<id>/ball-videos/
 *   GET  /api/admin/v2/matches/<id>/whole-match-video/
 *   POST /api/admin/v2/matches/<id>/start-multipart-upload-whole-match/
 *   PUT  <presigned_part_url>                (per part, direct to S3)
 *   POST /api/admin/v2/matches/<id>/complete-multipart-upload-whole-match/
 *   POST /api/admin/v2/matches/<id>/abort-multipart-upload-whole-match/
 *   POST /api/admin/v2/matches/<id>/tag-slice/
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Upload, Play, Pause, Scissors, Save, Edit3, Trash2,
  CheckCircle2, AlertCircle, RefreshCw, Video, ChevronRight, X,
  Loader2, CloudUpload,
} from "lucide-react";
import api from "../../api/axios";

// Raw axios PUT for S3 presigned URLs — the app-wide axios instance
// injects an Authorization header that would invalidate the presigned
// signature.
const axiosRawPut = (url, body, config) => axios.put(url, body, config);

// ── Time helpers ─────────────────────────────────────────────────────

function fmtMs(ms) {
  if (ms == null || Number.isNaN(ms)) return "—";
  const total = Math.max(0, Math.round(ms));
  const mins = Math.floor(total / 60000);
  const secs = ((total % 60000) / 1000).toFixed(3);
  return `${String(mins).padStart(2, "0")}:${secs.padStart(6, "0")}`;
}

function fmtBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
}

// ── localStorage persistence (offline fallback only) ────────────────
//
// 2026-07-31 — Primary source of truth is now Postgres + S3 via the
// per-ball /tag-ball/ endpoint. Every Save & Next posts the current
// ball's IN/OUT to the server and fires the slicer for that one
// ball, so tags survive browser close, cache clear, device switch,
// and incognito windows.
//
// localStorage stays as a belt-and-suspenders offline buffer: if a
// Save & Next POST fails (network blip, backend hiccup), the tag is
// still cached client-side and the operator can retry without losing
// the timestamp they just stamped. On mount we prefer server-side
// values over localStorage.

const TAG_STORAGE_PREFIX = "zcd:video-tagging:";

function loadTagsForMatch(matchId) {
  try {
    const raw = localStorage.getItem(TAG_STORAGE_PREFIX + matchId);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTagsForMatch(matchId, rows) {
  const map = {};
  for (const r of rows) {
    if (r._tagIn != null || r._tagOut != null) {
      map[r.ball_id] = { in: r._tagIn, out: r._tagOut };
    }
  }
  try {
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(TAG_STORAGE_PREFIX + matchId);
    } else {
      localStorage.setItem(TAG_STORAGE_PREFIX + matchId, JSON.stringify(map));
    }
  } catch {
    // Quota exceeded / privacy mode — swallow. The operator loses draft
    // state on refresh but can still tag + submit within the session.
  }
}

function clearTagsForMatch(matchId) {
  try { localStorage.removeItem(TAG_STORAGE_PREFIX + matchId); }
  catch { /* noop */ }
}


// ── Verbose error surfacer (mirrors MatchVideos.jsx pattern) ────────

function detailedError(phase, e) {
  if (e?.response) {
    const code = e.response.status;
    const data = e.response.data;
    if (typeof data === "string" && data.includes("<Code>")) {
      const codeM = /<Code>([^<]+)<\/Code>/.exec(data);
      const msgM  = /<Message>([^<]+)<\/Message>/.exec(data);
      return `[${phase}] S3 ${code}: ${(codeM && codeM[1]) || "?"} — ${(msgM && msgM[1]) || data.slice(0, 200)}`;
    }
    if (data && typeof data === "object") {
      if (data.detail) return `[${phase}] HTTP ${code}: ${data.detail}`;
      const parts = [];
      for (const [k, v] of Object.entries(data)) {
        parts.push(`${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
      }
      if (parts.length) return `[${phase}] HTTP ${code}: ${parts.join("; ")}`;
    }
    return `[${phase}] HTTP ${code}: ${(typeof data === "string" ? data.slice(0, 200) : "no body")}`;
  }
  return `[${phase}] ${e?.code || ""} ${e?.message || String(e)}`.trim();
}

// ═══════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════

export default function VideoTagging() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [matchMeta, setMatchMeta] = useState(null);
  const [rows, setRows] = useState([]);          // ball rows, +_tagIn/_tagOut
  const [currentIdx, setCurrentIdx] = useState(0);

  // Master state:
  //   s3_key      — set once the master is in S3 (either freshly uploaded
  //                 or rehydrated from the whole-match-video/ endpoint)
  //   localFile   — the File the operator picked, wrapped in an ObjectURL
  //                 so <video> can play it locally without a presigned GET
  //   objectUrl   — cached URL from URL.createObjectURL
  const [masterS3Key, setMasterS3Key] = useState(null);
  const [masterFilename, setMasterFilename] = useState("");
  const [masterSize, setMasterSize] = useState(0);
  // 2026-07-29 — presigned GET URL for the S3 master. Lets the tagger
  // play the video straight from S3 on a return visit, so the operator
  // doesn't need the original MP4 on their laptop 5 days later.
  const [masterPlaybackUrl, setMasterPlaybackUrl] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Clip preview lives in its own modal so the main tagging player
  // stays parked on the master MP4 (operator can flip between "tag
  // the next ball" and "preview a completed ball" without losing
  // their scroll / playhead position). Shape:
  //   { src, startMs, endMs, title }
  //   - src        — S3 clip URL post-submit; local master ObjectURL otherwise.
  //   - startMs    — seek offset (null for S3 clips whose full duration is the clip).
  //   - endMs      — auto-pause boundary (null when the clip IS the file).
  //   - title      — banner shown at the top of the modal.
  const [previewClip, setPreviewClip] = useState(null);

  const [loading, setLoading] = useState(true);

  // ── Fetch match + balls + whole-match state on mount ──────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const bv = await api.get(
        `/api/admin/v2/matches/${matchId}/ball-videos/`,
      );
      const d = bv.data || {};
      setMatchMeta({
        team1: d.team1, team2: d.team2,
        competition: d.competition, match_date: d.match_date,
        match_number: d.match_number,
        total_balls: d.total_balls, videos_linked: d.videos_linked,
      });
      // Hydrate in-progress tags from localStorage so a page refresh
      // (or navigating away and returning) doesn't wipe an
      // in-progress tagging session.
      const stored = loadTagsForMatch(matchId);
      const sorted = (d.rows || [])
        .slice()
        .sort((a, b) => (
          (a.innings - b.innings) ||
          (a.over - b.over) ||
          (a.ball - b.ball)
        ));

      // Remap ball_in_over to cricket convention: a wide / no-ball
      // shares the number of the FOLLOWING legal ball, so an over with
      // (legal, wide, legal, legal, legal, legal, legal) shows as
      // 0.1, 0.2, 0.2, 0.3, 0.4, 0.5, 0.6 — not 0.1..0.7. Legal ball
      // count resets at every over boundary and every innings.
      let curOverKey = "";
      let legalCount = 0;
      for (const r of sorted) {
        const key = `${r.innings}:${r.over}`;
        if (key !== curOverKey) { curOverKey = key; legalCount = 0; }
        if (r.is_legal) legalCount += 1;
        r._displayBall = r.is_legal ? legalCount : legalCount + 1;
      }

      // Prefer server-side timestamps over localStorage — a match the
      // operator half-tagged on their laptop yesterday shows up on any
      // browser with the exact same state today. localStorage only
      // wins for balls the server doesn't know about (offline retry).
      const seeded = sorted.map((r) => {
        const serverIn = r.video_start_ms;
        const serverOut = r.video_end_ms;
        const hasServer = serverIn != null && serverOut != null;
        return {
          ...r,
          _tagIn: hasServer ? serverIn : (stored[r.ball_id]?.in ?? null),
          _tagOut: hasServer ? serverOut : (stored[r.ball_id]?.out ?? null),
          // "server" = confirmed persisted in Postgres; ISO string =
          // this-session save; null = not yet saved server-side.
          _savedAt: hasServer ? "server" : null,
          _saving: false,
          _saveError: null,
        };
      });
      setRows(seeded);
      // Land on the first ball that hasn't been tagged yet — resumes
      // right where the operator left off.
      const firstUntagged = seeded.findIndex(
        (r) => r._tagIn == null || r._tagOut == null,
      );
      setCurrentIdx(
        seeded.length === 0 ? -1
          : (firstUntagged >= 0 ? firstUntagged : 0),
      );

      const wm = await api.get(
        `/api/admin/v2/matches/${matchId}/whole-match-video/`,
      );
      const w = wm.data || {};
      if (w.present && w.master) {
        setMasterS3Key(w.master.s3_key);
        setMasterFilename(w.master.filename || "(master)");
        setMasterSize(w.master.size_bytes || 0);
        setMasterPlaybackUrl(w.master.playback_url || null);
      }
    } catch (e) {
      setError(detailedError("load", e));
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Persist in-progress tags on every row mutation. The initial hydrate
  // in fetchAll() populates rows before this effect runs, so the first
  // fire re-writes whatever we just loaded — harmless.
  useEffect(() => {
    if (!matchId || !rows.length) return;
    saveTagsForMatch(matchId, rows);
  }, [rows, matchId]);

  // Cleanup any ObjectURL when we unmount / switch file.
  useEffect(() => () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  // ── Row helpers ────────────────────────────────────────────────────

  const taggedCount = useMemo(
    () => rows.filter((r) => r._tagIn != null && r._tagOut != null).length,
    [rows],
  );
  const allTagged = rows.length > 0 && taggedCount === rows.length;
  const currentRow = currentIdx >= 0 && currentIdx < rows.length ? rows[currentIdx] : null;

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const advanceToNextUntagged = () => {
    if (!rows.length) return;
    const n = rows.length;
    for (let step = 1; step <= n; step++) {
      const j = (currentIdx + step) % n;
      if (rows[j]._tagIn == null || rows[j]._tagOut == null) {
        setCurrentIdx(j);
        return;
      }
    }
    setCurrentIdx(Math.min(currentIdx + 1, n - 1));
  };

  // ── Player controls ───────────────────────────────────────────────

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const nudge = (deltaMs) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, (v.currentTime * 1000 + deltaMs) / 1000);
  };

  const seekTo = (ms) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, ms / 1000);
  };

  // ── Preview a tagged ball in a popup ─────────────────────────────
  //
  // Prefer the server-side per-ball clip URL when the operator has
  // already submitted (row.video_url populated by the ball-videos
  // endpoint) — that's the exact S3-hosted MP4 the highlights player
  // shows, so previewing from there confirms the slicer produced what
  // we wanted. Otherwise fall back to the local master ObjectURL with
  // startMs/endMs so the modal seeks + auto-pauses on the boundary.

  const previewBall = (idx) => {
    if (idx < 0 || idx >= rows.length) return;
    const r = rows[idx];
    if (r._tagIn == null || r._tagOut == null) return;
    const label =
      `Inn ${r.innings} · Over ${r.over}.${r._displayBall ?? r.ball}` +
      (r.striker ? ` · ${r.striker}` : "") +
      (r.bowler ? ` vs ${r.bowler}` : "");

    // Post-submit path: play the S3 clip directly. Its whole duration
    // IS the clip so no seek / auto-pause boundary.
    if (r.video_url) {
      setPreviewClip({
        src: r.video_url,
        startMs: null,
        endMs: null,
        title: label,
        source: "s3",
      });
      return;
    }

    // Pre-submit path — need the local master loaded.
    if (!objectUrl) {
      setError(
        "Load the local full-match file (Load local file for tagging) " +
        "to preview clips before submitting them.",
      );
      return;
    }
    setPreviewClip({
      src: objectUrl,
      startMs: r._tagIn,
      endMs: r._tagOut,
      title: label,
      source: "local",
    });
  };

  // ── Tagging actions ───────────────────────────────────────────────

  // videoSrc — whichever source the <video> element is currently bound
  // to. Local objectUrl wins for scrub smoothness; the S3 presigned URL
  // is the fallback that lets the operator tag without a local file.
  const videoSrc = objectUrl || masterPlaybackUrl;

  const markIn = () => {
    if (!currentRow || !videoSrc) return;
    const v = videoRef.current;
    if (!v) return;
    updateRow(currentIdx, { _tagIn: Math.round(v.currentTime * 1000) });
  };

  const markOut = () => {
    if (!currentRow || !videoSrc) return;
    const v = videoRef.current;
    if (!v) return;
    updateRow(currentIdx, { _tagOut: Math.round(v.currentTime * 1000) });
  };

  // Push ONE ball's IN/OUT to the backend. The endpoint upserts the
  // BallVideo row and fires the slicer for this ball only, so the
  // clip lands in S3 within ~2 s and future re-tagging (edit) hits
  // the same deterministic key and overwrites in place.
  const saveTagToBackend = useCallback(async (idx) => {
    if (idx < 0 || idx >= rows.length) return { ok: false };
    const r = rows[idx];
    if (r._tagIn == null || r._tagOut == null) {
      return { ok: false, reason: "Set both clip IN and clip OUT before saving." };
    }
    if (r._tagOut <= r._tagIn) {
      return { ok: false, reason: "Clip OUT must be after clip IN." };
    }
    if (!masterS3Key) {
      return {
        ok: false,
        reason: "Upload the full-match MP4 to S3 first — per-ball slicer needs the master.",
      };
    }
    updateRow(idx, { _saving: true, _saveError: null });
    try {
      const resp = await api.post(
        `/api/admin/v2/matches/${matchId}/tag-ball/`,
        {
          video_source_id: masterS3Key,
          ball_id: r.ball_id,
          start_ms: r._tagIn,
          end_ms: r._tagOut,
        },
      );
      const d = resp.data || {};
      updateRow(idx, {
        _saving: false,
        _savedAt: new Date().toISOString(),
        _saveError: null,
        // Reflect server-canonical values on the row so a subsequent
        // page refresh shows the same thing without a full refetch.
        video_id: d.video_id || r.video_id,
        video_state: d.state || "processing",
        video_start_ms: d.video_start_ms,
        video_end_ms: d.video_end_ms,
      });
      return { ok: true };
    } catch (e) {
      const msg = detailedError("tag-ball", e);
      updateRow(idx, { _saving: false, _saveError: msg });
      return { ok: false, reason: msg };
    }
  }, [rows, masterS3Key, matchId]);

  const saveAndNext = useCallback(async () => {
    if (!currentRow) return;
    const idx = currentIdx;
    const result = await saveTagToBackend(idx);
    if (!result.ok) {
      setError(result.reason || "Save failed.");
      return;
    }
    setError(null);
    advanceToNextUntagged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRow, currentIdx, saveTagToBackend]);

  const editBall = (idx) => {
    if (idx < 0 || idx >= rows.length) return;
    setCurrentIdx(idx);
    const r = rows[idx];
    if (r._tagIn != null) {
      queueMicrotask(() => {
        const v = videoRef.current;
        if (v) v.currentTime = r._tagIn / 1000;
      });
    }
  };

  const clearTag = (idx) => {
    updateRow(idx, { _tagIn: null, _tagOut: null });
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in an input.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "[") { e.preventDefault(); markIn(); }
      else if (e.key === "]") { e.preventDefault(); markOut(); }
      else if (e.code === "Space") { e.preventDefault(); togglePlay(); }
      else if (e.key === "Enter") { e.preventDefault(); saveAndNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, rows, objectUrl, masterPlaybackUrl]);

  // ── Upload master via multipart ────────────────────────────────────

  const onPickFile = async (file) => {
    if (!file) return;
    setError(null); setNotice(null);
    // Always wrap for local playback FIRST so the operator can start
    // tagging even while the upload runs in the background.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    setMasterFilename(file.name);
    setMasterSize(file.size);

    // Skip upload when the master is already in S3 and the operator
    // is just re-picking the file locally to play it.
    if (masterS3Key) {
      setNotice(
        `Loaded ${file.name} for local playback. Master already in S3 — ` +
        "no re-upload needed.",
      );
      return;
    }
    await uploadMaster(file);
  };

  const uploadMaster = async (file) => {
    setUploading(true);
    setUploadProgress({ pct: 0, phase: "start-multipart", loaded: 0, total: file.size });
    let mp = null;
    try {
      // 1) Kick off multipart — same endpoint MatchVideos.jsx uses.
      const start = await api.post(
        `/api/admin/v2/matches/${matchId}/start-multipart-upload-whole-match/`,
        { filename: file.name, total_size: file.size },
      );
      mp = start.data;
      const { upload_id, s3_key_within, part_size, part_urls } = mp;

      // 2) PUT parts serially, capture ETags.
      setUploadProgress({ pct: 0, phase: "uploading", loaded: 0, total: file.size });
      const parts = [];
      let sentBefore = 0;
      for (const { part_number, url } of part_urls) {
        const from = (part_number - 1) * part_size;
        const to = Math.min(from + part_size, file.size);
        const blob = file.slice(from, to);
        const r = await axiosRawPut(url, blob, {
          onUploadProgress: (ev) => {
            const partLoaded = ev.loaded || 0;
            const totalLoaded = sentBefore + partLoaded;
            const pct = Math.min(100, Math.floor((totalLoaded / file.size) * 100));
            setUploadProgress({
              pct, phase: "uploading",
              loaded: totalLoaded, total: file.size,
            });
          },
        });
        const etag = r.headers?.etag || r.headers?.ETag || "";
        parts.push({ PartNumber: part_number, ETag: etag });
        sentBefore += (to - from);
      }

      // 3) Complete.
      setUploadProgress({ pct: 100, phase: "finalizing", loaded: file.size, total: file.size });
      const complete = await api.post(
        `/api/admin/v2/matches/${matchId}/complete-multipart-upload-whole-match/`,
        { upload_id, s3_key_within, parts },
      );
      setMasterS3Key(complete.data.s3_key);
      setNotice(`Full-match video stored in S3 (${fmtBytes(file.size)}).`);
    } catch (e) {
      setError(detailedError("master-upload", e));
      // Best-effort abort so we don't leak the multipart upload.
      if (mp?.upload_id && mp?.s3_key_within) {
        try {
          await api.post(
            `/api/admin/v2/matches/${matchId}/abort-multipart-upload-whole-match/`,
            { upload_id: mp.upload_id, s3_key_within: mp.s3_key_within },
          );
        } catch { /* non-fatal */ }
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // ── Submit tag-slice ───────────────────────────────────────────────

  const onSubmit = async () => {
    if (!masterS3Key) {
      setError("Master MP4 hasn't been uploaded to S3 yet.");
      return;
    }
    if (!allTagged) {
      setError(`${rows.length - taggedCount} ball(s) still untagged.`);
      return;
    }
    setSubmitting(true); setError(null); setNotice(null);
    try {
      const body = {
        video_source_id: masterS3Key,
        balls: rows.map((r) => ({
          ball_id: r.ball_id,
          start_ms: r._tagIn,
          end_ms: r._tagOut,
        })),
      };
      const r = await api.post(
        `/api/admin/v2/matches/${matchId}/tag-slice/`, body,
      );
      const info = r.data || {};
      setNotice(
        `Persisted ${info.rows_written} BallVideo rows. Slicer dispatched ` +
        `${info.slicer?.submitted || 0} clips via ${info.slicer?.backend || "?"}. ` +
        "Clips will materialise in S3 over the next ~30 s.",
      );
    } catch (e) {
      setError(detailedError("tag-slice", e));
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="p-6 text-text-secondary">Loading match…</div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> back
          </button>
          <h1 className="text-2xl font-semibold mt-1 flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-400" />
            Video tagging
          </h1>
          {matchMeta && (
            <p className="mt-1 text-sm text-text-primary">
              <span className="font-medium">
                {matchMeta.team1} vs {matchMeta.team2}
              </span>
              {matchMeta.competition && (
                <span className="text-text-secondary"> · {matchMeta.competition}</span>
              )}
              {matchMeta.match_date && (
                <span className="text-text-secondary"> · {matchMeta.match_date}</span>
              )}
            </p>
          )}
          <p className="mt-1 text-xs text-text-secondary max-w-2xl">
            Upload the full-match MP4, scrub the timeline and press{" "}
            <kbd className="px-1 bg-bg-tertiary rounded">[</kbd> and{" "}
            <kbd className="px-1 bg-bg-tertiary rounded">]</kbd> to mark clip
            IN / OUT for each ball, then Save & next. The backend slices per-ball
            MP4s at the deterministic keys used by the highlights player.
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-semibold">{taggedCount}/{rows.length}</div>
          <div className="text-xs text-text-secondary">balls tagged</div>
          <Link
            to={`/admin/matches/${matchId}/videos`}
            className="mt-2 inline-flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200"
          >
            Ball Videos page <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* ── Master upload / load panel ─────────────────────────────── */}
      <MasterPanel
        masterS3Key={masterS3Key}
        masterFilename={masterFilename}
        masterSize={masterSize}
        masterPlaybackUrl={masterPlaybackUrl}
        objectUrl={objectUrl}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onPickFile={onPickFile}
      />

      {/* ── Notice / error banners ─────────────────────────────────── */}
      {notice && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="border border-rose-500/40 bg-rose-500/10 text-rose-200 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Player + tagging controls ──────────────────────────────── */}
      {videoSrc && (
        <PlayerPanel
          videoRef={videoRef}
          videoSrc={videoSrc}
          currentRow={currentRow}
          currentIdx={currentIdx}
          canSave={!!masterS3Key}
          onMarkIn={markIn}
          onMarkOut={markOut}
          onSaveAndNext={saveAndNext}
          onTogglePlay={togglePlay}
          onNudge={nudge}
          onSeekTo={seekTo}
          playerTime={playerTime}
          playerDuration={playerDuration}
          playing={playing}
          setPlayerTime={setPlayerTime}
          setPlayerDuration={setPlayerDuration}
          setPlaying={setPlaying}
        />
      )}

      {/* ── Ball table ─────────────────────────────────────────────── */}
      <BallTable
        rows={rows}
        currentIdx={currentIdx}
        onEdit={editBall}
        onClear={clearTag}
        onPreview={previewBall}
      />

      {/* ── Submit ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={fetchAll}
          disabled={loading || uploading || submitting}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-border-default text-text-primary text-sm hover:bg-bg-secondary disabled:opacity-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        {taggedCount > 0 && (
          <button
            onClick={() => {
              if (!window.confirm(
                `Clear all ${taggedCount} tagged clip(s) on this match?\n\n` +
                "This wipes the in-progress IN/OUT stamps stored locally " +
                "in this browser. It does NOT touch any BallVideo rows " +
                "already submitted to the server.",
              )) return;
              clearTagsForMatch(matchId);
              setRows((prev) => prev.map((r) => (
                { ...r, _tagIn: null, _tagOut: null }
              )));
              setCurrentIdx(rows.length ? 0 : -1);
              setNotice("Local tags cleared.");
            }}
            disabled={loading || uploading || submitting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-rose-500/40 text-rose-300 text-sm hover:bg-rose-500/10 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear tags
          </button>
        )}
        <button
          onClick={onSubmit}
          disabled={!allTagged || !masterS3Key || submitting}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Scissors className="w-4 h-4" />
          {submitting
            ? "Dispatching slicer…"
            : `Slice & upload clips (${taggedCount})`}
        </button>
      </div>

      {/* ── Clip preview popup ─────────────────────────────────────── */}
      {previewClip && (
        <ClipPreviewModal
          clip={previewClip}
          onClose={() => setPreviewClip(null)}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Master upload / load panel
// ═══════════════════════════════════════════════════════════════════

function MasterPanel({
  masterS3Key, masterFilename, masterSize, masterPlaybackUrl, objectUrl,
  uploading, uploadProgress, onPickFile,
}) {
  const inputRef = useRef(null);
  const hasS3 = !!masterS3Key;
  const hasLocal = !!objectUrl;
  const canStream = !!masterPlaybackUrl;

  return (
    <div className="border border-border-default rounded-lg p-4 bg-bg-secondary/30">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-2">
            <Video className="w-4 h-4" />
            Full-match video
          </div>
          <div className="text-xs text-text-secondary mt-0.5 truncate">
            {hasS3
              ? `${masterFilename} · ${fmtBytes(masterSize)} · in S3 ✓`
              : "Not uploaded yet."}
          </div>
          {hasS3 && canStream && !hasLocal && (
            <div className="text-[11px] text-emerald-300 mt-1">
              Streaming from S3 — you can tag without picking a local file.
              For smoother scrubbing on slow connections, load the same MP4
              from disk below (we'll skip the upload).
            </div>
          )}
          {hasS3 && !canStream && !hasLocal && (
            <div className="text-[11px] text-amber-300 mt-1">
              Master in S3 but the presigned playback URL is missing — pick
              the SAME file below to tag (upload will be skipped).
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/x-matroska"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPickFile(f);
              e.target.value = "";
            }}
          />
          <button
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {hasS3
              ? (hasLocal ? "Replace local file" : "Load local file (optional)")
              : "Upload full-match MP4"}
          </button>
        </div>
      </div>
      {uploadProgress && (
        <div className="mt-3">
          <div className="h-2 bg-bg-tertiary rounded overflow-hidden">
            <div
              className="h-full bg-purple-500 transition-all"
              style={{ width: `${uploadProgress.pct || 0}%` }}
            />
          </div>
          <div className="text-[11px] text-text-secondary mt-1 flex justify-between">
            <span>{uploadProgress.phase} — {uploadProgress.pct || 0}%</span>
            <span>
              {fmtBytes(uploadProgress.loaded || 0)} / {fmtBytes(uploadProgress.total || 0)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Player + tagging controls
// ═══════════════════════════════════════════════════════════════════

function PlayerPanel({
  videoRef, videoSrc, currentRow, currentIdx, canSave,
  onMarkIn, onMarkOut, onSaveAndNext, onTogglePlay, onNudge, onSeekTo,
  playerTime, playerDuration, playing,
  setPlayerTime, setPlayerDuration, setPlaying,
}) {
  const saving = !!currentRow?._saving;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Player — spans 2/3 on lg */}
      <div className="lg:col-span-2 border border-border-default rounded-lg overflow-hidden bg-black">
        <video
          ref={videoRef}
          src={videoSrc}
          className="w-full aspect-video bg-black"
          onTimeUpdate={(e) => setPlayerTime(Math.round(e.target.currentTime * 1000))}
          onLoadedMetadata={(e) => setPlayerDuration(Math.round(e.target.duration * 1000))}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          controls={false}
        />
        {/* Timeline scrubber */}
        <div className="p-2 bg-bg-primary border-t border-border-default">
          <input
            type="range"
            min="0"
            max={playerDuration || 0}
            step="10"
            value={playerTime}
            onChange={(e) => onSeekTo(parseInt(e.target.value, 10))}
            className="w-full accent-purple-500"
          />
          <div className="flex justify-between text-[11px] font-mono text-text-secondary mt-1">
            <span>{fmtMs(playerTime)}</span>
            <span>{fmtMs(playerDuration)}</span>
          </div>
        </div>
        {/* Transport row */}
        <div className="p-2 bg-bg-primary border-t border-border-default flex items-start gap-2 flex-wrap">
          {/* Left group: playback transport */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onTogglePlay}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-bg-tertiary hover:bg-bg-secondary text-sm"
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {playing ? "Pause" : "Play"}
            </button>
            {[
              [-5000, "⏪ 5s"], [-1000, "◀ 1s"],
              [1000, "1s ▶"], [5000, "5s ⏩"],
            ].map(([d, label]) => (
              <button
                key={d}
                onClick={() => onNudge(d)}
                className="px-2 py-1.5 rounded bg-bg-tertiary hover:bg-bg-secondary text-xs"
              >{label}</button>
            ))}
          </div>
          <div className="grow" />
          {/* Right column: Clip IN + Clip OUT on row 1, Save & next
              stacked directly below (per founder request 2026-07-24 —
              the operator's eye stays anchored to the same spot after
              stamping the OUT, so Save & next lands under their thumb). */}
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={onMarkIn}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-amber-600/80 hover:bg-amber-500 text-white text-sm"
              >
                <Scissors className="w-3.5 h-3.5" /> Clip IN [
              </button>
              <button
                onClick={onMarkOut}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-amber-600/80 hover:bg-amber-500 text-white text-sm"
              >
                <Scissors className="w-3.5 h-3.5" /> Clip OUT ]
              </button>
            </div>
            <button
              onClick={onSaveAndNext}
              disabled={saving || !canSave}
              title={
                !canSave
                  ? "Upload the full-match MP4 to S3 first (Save & Next now slices the clip immediately)."
                  : saving
                    ? "Saving to S3…"
                    : "Save this ball's IN/OUT to Postgres + fire the slicer, then advance."
              }
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Save className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save & next"}
            </button>
          </div>
        </div>
      </div>

      {/* Side panel — current ball info + tag state */}
      <div className="border border-border-default rounded-lg p-4 bg-bg-secondary/30 space-y-3">
        <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold">
          Currently tagging
        </div>
        {currentRow ? (
          <>
            <div className="text-lg font-semibold">
              Inn {currentRow.innings} · Over {currentRow.over}.{currentRow._displayBall ?? currentRow.ball}
              {currentRow.is_legal === false && (
                <span className="ml-2 text-xs uppercase tracking-wider text-amber-300">
                  extra
                </span>
              )}
            </div>
            <div className="text-sm text-text-secondary">
              <div>Striker: <span className="text-text-primary">{currentRow.striker || "—"}</span></div>
              <div>Bowler: <span className="text-text-primary">{currentRow.bowler || "—"}</span></div>
              <div>
                Runs: <span className="text-text-primary">{currentRow.runs_off_bat || 0}</span>
                {currentRow.extras ? <span className="text-text-secondary"> +{currentRow.extras}</span> : null}
                {currentRow.wicket_type ? <span className="text-rose-400 ml-2">W</span> : null}
              </div>
            </div>
            <div className="border-t border-border-default pt-2 mt-2 space-y-1 text-sm font-mono">
              <div className="flex justify-between">
                <span className="text-text-secondary">Clip IN:</span>
                <span>{fmtMs(currentRow._tagIn)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Clip OUT:</span>
                <span>{fmtMs(currentRow._tagOut)}</span>
              </div>
              {currentRow._tagIn != null && currentRow._tagOut != null && (
                <div className="flex justify-between text-emerald-300">
                  <span>Duration:</span>
                  <span>{fmtMs(currentRow._tagOut - currentRow._tagIn)}</span>
                </div>
              )}
            </div>
            {/* Per-ball save status — Save & Next posts to /tag-ball/ */}
            <SaveStatusBadge row={currentRow} />
            <div className="text-[11px] text-text-secondary pt-2">
              Ball {currentIdx + 1} of the tagging sequence.
            </div>
          </>
        ) : (
          <div className="text-sm text-text-secondary">No ball selected.</div>
        )}
        <div className="pt-3 border-t border-border-default text-[11px] text-text-secondary space-y-0.5">
          <div>Shortcuts:</div>
          <div><kbd className="px-1 bg-bg-tertiary rounded">[</kbd> Clip IN · <kbd className="px-1 bg-bg-tertiary rounded">]</kbd> Clip OUT</div>
          <div><kbd className="px-1 bg-bg-tertiary rounded">Space</kbd> Play/pause · <kbd className="px-1 bg-bg-tertiary rounded">Enter</kbd> Save & next</div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Save-status badge — mirrors the per-ball _saving / _savedAt /
// _saveError shape produced by saveTagToBackend().
// ═══════════════════════════════════════════════════════════════════

function SaveStatusBadge({ row }) {
  if (!row) return null;
  const { _saving, _saveError, _savedAt } = row;
  if (_saving) {
    return (
      <div className="mt-2 text-[11px] inline-flex items-center gap-1.5 text-sky-300">
        <Loader2 className="w-3 h-3 animate-spin" /> Saving to S3…
      </div>
    );
  }
  if (_saveError) {
    return (
      <div className="mt-2 text-[11px] inline-flex items-start gap-1.5 text-rose-300 max-w-full">
        <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
        <span className="break-words">Save failed — {_saveError}</span>
      </div>
    );
  }
  if (_savedAt) {
    const label = _savedAt === "server" ? "Saved (Postgres + S3)" : "Saved · slicer dispatched";
    return (
      <div className="mt-2 text-[11px] inline-flex items-center gap-1.5 text-emerald-300">
        <CheckCircle2 className="w-3 h-3" /> {label}
      </div>
    );
  }
  return (
    <div className="mt-2 text-[11px] inline-flex items-center gap-1.5 text-text-secondary">
      <CloudUpload className="w-3 h-3" /> Not saved yet
    </div>
  );
}


function RowSaveIcon({ row }) {
  if (row._saving) {
    return <Loader2 className="w-3 h-3 animate-spin text-sky-400 inline" />;
  }
  if (row._saveError) {
    return (
      <AlertCircle
        className="w-3 h-3 text-rose-400 inline"
        title={row._saveError}
      />
    );
  }
  if (row._savedAt) {
    return (
      <CheckCircle2
        className="w-3 h-3 text-emerald-400 inline"
        title={row._savedAt === "server" ? "Persisted (Postgres + S3)" : "Saved this session"}
      />
    );
  }
  return null;
}


// ═══════════════════════════════════════════════════════════════════
// Ball table
// ═══════════════════════════════════════════════════════════════════

function BallTable({ rows, currentIdx, onEdit, onClear, onPreview }) {
  return (
    <div className="border border-border-default rounded-lg overflow-hidden">
      <div className="px-4 py-2 border-b border-border-default bg-bg-secondary/50 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold">
          Ball-by-ball
        </div>
        <div className="text-xs text-text-secondary">
          {rows.length} balls
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-text-secondary sticky top-0 bg-bg-secondary/90 backdrop-blur">
            <tr>
              <th className="px-3 py-2 text-left w-8"></th>
              <th className="px-3 py-2 text-left">Inn</th>
              <th className="px-3 py-2 text-left">O.B</th>
              <th className="px-3 py-2 text-left">Striker</th>
              <th className="px-3 py-2 text-left">Bowler</th>
              <th className="px-3 py-2 text-left">Runs</th>
              <th className="px-3 py-2 text-left">Start</th>
              <th className="px-3 py-2 text-left">End</th>
              <th className="px-3 py-2 text-left">Dur</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const isCurrent = idx === currentIdx;
              const isTagged = r._tagIn != null && r._tagOut != null;
              return (
                <tr
                  key={r.ball_id}
                  onClick={() => isTagged && onPreview?.(idx)}
                  onDoubleClick={() => onEdit(idx)}
                  className={
                    "border-t border-border-default cursor-pointer " +
                    (isCurrent
                      ? "bg-purple-500/15 text-text-primary"
                      : "hover:bg-bg-secondary/50")
                  }
                  title={isTagged ? "Click to preview · double-click to edit" : "Double-click to jump the tagger here"}
                >
                  <td className="px-3 py-2">
                    {isCurrent && <span className="text-purple-400">▶</span>}
                  </td>
                  <td className="px-3 py-2">{r.innings}</td>
                  <td className="px-3 py-2 font-mono">
                    {r.over}.{r._displayBall ?? r.ball}
                    {r.is_legal === false && (
                      <span
                        className="ml-1 text-[9px] uppercase tracking-wider text-amber-300 align-top"
                        title="Extra (wide / no-ball) — shares the number of the following legal delivery."
                      >
                        ex
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 truncate max-w-[180px]">{r.striker}</td>
                  <td className="px-3 py-2 truncate max-w-[180px]">{r.bowler}</td>
                  <td className="px-3 py-2">
                    {r.runs_off_bat || 0}
                    {r.extras ? <span className="text-text-secondary"> +{r.extras}</span> : null}
                    {r.wicket_type ? <span className="text-rose-400 ml-1">W</span> : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {isTagged ? (
                      <span className="text-emerald-300">{fmtMs(r._tagIn)}</span>
                    ) : (
                      <span className="text-text-secondary">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {isTagged ? (
                      <span className="text-emerald-300">{fmtMs(r._tagOut)}</span>
                    ) : (
                      <span className="text-text-secondary">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {isTagged ? (
                      <span className="text-emerald-400 inline-flex items-center gap-1">
                        {fmtMs(r._tagOut - r._tagIn)}
                        <RowSaveIcon row={r} />
                      </span>
                    ) : (
                      <span className="text-text-secondary">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isTagged && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onPreview?.(idx); }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        title={r.video_url
                          ? "Preview the S3-hosted clip (already saved)"
                          : "Preview from the local master (submit to save to S3)"}
                      >
                        <Play className="w-3 h-3" /> Preview
                        {r.video_url && <span className="ml-0.5 text-[9px] uppercase text-emerald-400">S3</span>}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(idx); }}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-border-default hover:bg-bg-secondary"
                      title="Jump the tagger to this ball"
                    >
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    {isTagged && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onClear(idx); }}
                        className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        title="Clear this ball's IN/OUT"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-text-secondary">
                  No balls found for this match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Clip preview modal — plays IN→OUT in a lightbox, mirrors the
// MatchVideos.jsx cross-check dialog pattern so the founder sees the
// same overlay + close behaviour everywhere.
// ═══════════════════════════════════════════════════════════════════

function ClipPreviewModal({ clip, onClose }) {
  const videoRef = useRef(null);
  const { src, startMs, endMs, title, source } = clip;

  // Seek + auto-pause. For local-master clips we start at startMs and
  // pause the moment currentTime crosses endMs; for S3 clips the file
  // IS the clip so we play from 0 and let the browser end it.
  const onLoadedMetadata = (e) => {
    if (startMs != null) {
      e.target.currentTime = startMs / 1000;
    }
    // Chromium requires an explicit play() call for programmatically
    // opened dialogs; autoplay attribute alone may be blocked.
    e.target.play().catch(() => { /* autoplay policy — noop */ });
  };

  const onTimeUpdate = (e) => {
    if (endMs == null) return;
    if (e.target.currentTime * 1000 >= endMs) {
      e.target.pause();
    }
  };

  // ESC to close, click-outside to close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const duration = endMs != null && startMs != null
    ? (endMs - startMs) / 1000
    : null;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ball clip preview"
        className="bg-bg-primary border border-border-default rounded-lg p-3 max-w-3xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="text-sm">
            <span className="font-medium text-text-primary">{title}</span>
            {source === "s3" && (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
                S3 clip
              </span>
            )}
            {source === "local" && (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300">
                Local master
              </span>
            )}
            {duration != null && (
              <span className="ml-2 text-xs text-text-secondary font-mono">
                {duration.toFixed(2)} s
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-bg-secondary"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <video
          ref={videoRef}
          src={src}
          controls
          autoPlay
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={onTimeUpdate}
          className="w-full max-h-[70vh] bg-black rounded"
        />
      </div>
    </div>
  );
}
